import { useCallback, useEffect, useRef, useState } from 'react';
import { IIntersectionObserverInstance, intersectionObserverService } from '../utils/intersection-observer';

export const useImageVisible = ({ onImageVisible }: { onImageVisible?: (el: HTMLImageElement) => void }) => {
  const [pollContainerEl] = useState(() => document.getElementById('root'));

  const state = useRef({ viewport: false, poll: false, loaded: false });
  const imageRef = useRef<HTMLImageElement>(null);

  const observerViewport = useRef<IIntersectionObserverInstance>();
  const observerContainer = useRef<IIntersectionObserverInstance>();

  const handleHasShownIfReady = useCallback(() => {
    if (onImageVisible && Object.values(state.current).every(v => v) && imageRef.current) {
      onImageVisible(imageRef.current);

      observerContainer.current?.disconnect();
      observerViewport.current?.disconnect();
    }
  }, []);

  useEffect(() => {
    if (!imageRef.current || !onImageVisible) return;

    const options = { rootMargin: '0px 0px 0px 0px', threshold: 0 };
    const handleOnObserve =
      (containerType: 'viewport' | 'poll') =>
      ([entry]: IntersectionObserverEntry[]) => {
        if (entry?.isIntersecting && entry.boundingClientRect.height > 0) {
          state.current[containerType] = true;

          handleHasShownIfReady();
        }
      };

    observerViewport.current = intersectionObserverService.getInstance(handleOnObserve('viewport'), options);
    observerContainer.current = intersectionObserverService.getInstance(handleOnObserve('poll'), {
      ...options,
      root: pollContainerEl,
    });

    observerViewport.current.observe(imageRef.current);
    observerContainer.current.observe(imageRef.current);

    return () => {
      observerContainer.current?.disconnect();
      observerViewport.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const image = imageRef.current;

    if (!image || !onImageVisible) return;

    const handleOnLoad = () => {
      state.current.loaded = true;
      handleHasShownIfReady();
    };

    if (image.complete) {
      handleOnLoad();
      return;
    }

    image.addEventListener('load', handleOnLoad);

    return () => {
      image.removeEventListener('load', handleOnLoad);
    };
  }, []);

  return { imageRef };
};
